/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { ModelHandler } from "./ModelHandler";
import { TValidatonError } from "./IValidationError";
import { EventEmitter } from "../EventEmitter";
import { IModelInputState } from "./IModelInputState";

export interface IModelInputBaseConfig {
	/** If the field is required */
	required?: boolean;

	/** Input label */
	label?: string;

	/** Input description */
	description?: string;

	/** Input text suffix */
	suffix?: string;
}

/**
 * Model Input Field Base Class
 */
export abstract class ModelInput<InputType, ValueType, ConfigType extends IModelInputBaseConfig>
	extends EventEmitter<"update", IModelInputState<InputType, ValueType, ConfigType>> {

	/** Model handler */
	private handler: ModelHandler;

	/** Field type */
	private type: string;

	/** Initial input value */
	private initialValue: InputType;

	/** Field input value (from html element) */
	private inputValue: InputType;

	/** Parsed and sanitized value */
	private value: ValueType;

	/** Additional field configuration */
	protected config: ConfigType;

	/** If the field value is valid */
	private valid: boolean = false;

	/** If the field value is pristine (eg. if it was changed) */
	private pristine: boolean = true;

	/** If user touches the field */
	private touched: boolean = false;

	/** Validation errors */
	private validationErrors: Array<TValidatonError> = [];

	/**
	 * Field constructor
	 *
	 * @param handler Handler instance
	 * @param type Field type
	 * @param initialValue Initial value
	 */
	public constructor(
		handler: ModelHandler,
		type: string,
		initialValue: InputType,
		config: ConfigType
	) {

		super();

		this.handler = handler;
		this.type = type;
		this.config = config;
		this.initialValue = initialValue;

		this.reset(true, false);

	}

	/**
	 * Parse input value into real value - should be overriden
	 *
	 * @param inputValue New input value
	 */
	protected abstract parse?(inputValue: InputType): ValueType;

	/**
	 * Validates the input
	 *
	 * @param inputValue New input value
	 */
	protected abstract validate?(inputValue: InputType, value: ValueType): Array<TValidatonError>;

	/**
	 * Sets new field value
	 *
	 * @param newValue New value
	 * @param setPristine If to reset modified state
	 * @param update If to update entire model
	 */
	public set(newValue: InputType, setPristine: boolean = false, update: boolean = true) {

		this.inputValue = newValue;

		if (this.parse) {
			this.value = this.parse(newValue);
		} else {
			this.value = (newValue as any) as ValueType;
		}

		if (this.validate) {
			this.validationErrors = this.validate(newValue, this.value);
			this.valid = this.validationErrors.length === 0 ? true : false;
		} else {
			this.valid = true;
		}

		if (setPristine) {
			this.pristine = true;
			this.touched = false;
		} else {
			this.pristine = false;
			this.touched = true;
		}

		this.emit("update", this.getState());

		if (update) {
			this.handler.update();
		}

	}

	/**
	 * Resets value to initial state
	 *
	 * @param setPristine If to reset modified state
	 * @param update If to update entire model
	 */
	public reset(setPristine: boolean = true, update: boolean = true) {

		this.set(this.initialValue, setPristine, update);

	}

	/**
	 * Set the field as touched
	 */
	public touch() {

		this.touched = true;

	}

	/**
	 * Returns field value
	 */
	public get() {

		return this.value;

	}

	/**
	 * Returns input value
	 */
	public getInputValue() {

		return this.inputValue;

	}

	/**
	 * Returns field specification for UI
	 */
	public getState(): IModelInputState<InputType, ValueType, ConfigType> {

		return {
			type: this.type,
			inputValue: this.inputValue,
			value: this.value,
			config: this.config,
			valid: this.valid,
			validationErrors: this.validationErrors,
			pristine: this.pristine,
			touched: this.touched
		};

	}

}
